import { useState, useEffect, memo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Wallet, Copy, CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { toast } from 'sonner';
import { addUTXOAddress, getUTXOAddresses, removeUTXOAddress, useWallet } from '../contexts/WalletContext';
import { isAtomicSwapCompatible } from '../lib/atomicSwapUtils';

interface AddUTXOAddressesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const UTXO_TOKENS = [
  { symbol: 'RVN', name: 'Ravencoin', placeholder: 'Rxxxxxxxxxxxxx...', example: 'R9z5PyfQb4NVpFktR6EWpUVXft3XjrpDtQ' },
  { symbol: 'BTC', name: 'Bitcoin', placeholder: 'bc1xxxxxxxxxxxxx...', example: 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh' },
  { symbol: 'LTC', name: 'Litecoin', placeholder: 'Lxxxxxxxxxxxxx...', example: 'LTCxxxxxxxxxxxxx...' },
  { symbol: 'DOGE', name: 'Dogecoin', placeholder: 'Dxxxxxxxxxxxxx...', example: 'Dxxxxxxxxxxxxx...' },
  { symbol: 'BCH', name: 'Bitcoin Cash', placeholder: 'bitcoincash:...', example: 'bitcoincash:...' },
];

function AddUTXOAddressesModal({ open, onOpenChange }: AddUTXOAddressesModalProps) {
  const { refreshBalances, walletType } = useWallet();
  const [addresses, setAddresses] = useState<Record<string, string>>({});
  const [savedAddresses, setSavedAddresses] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      // Load existing addresses
      const existing = getUTXOAddresses();
      setSavedAddresses(existing);
      setAddresses(existing);
    }
  }, [open]);

  const handleAddressChange = (token: string, value: string) => {
    setAddresses(prev => ({ ...prev, [token]: value }));
  };

  const handleSave = async () => {
    try {
      // Save all addresses
      for (const [token, address] of Object.entries(addresses)) {
        if (address && address.trim()) {
          addUTXOAddress(token, address.trim());
        } else {
          // Remove if empty
          removeUTXOAddress(token);
        }
      }

      // Refresh balances
      await refreshBalances();

      toast.success('Addresses Saved', {
        description: 'Your UTXO addresses have been saved. Balances will update shortly.',
        duration: 5000,
      });

      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to Save', {
        description: 'Could not save addresses. Please try again.',
        duration: 5000,
      });
    }
  };

  const handleRemove = (token: string) => {
    removeUTXOAddress(token);
    setAddresses(prev => {
      const updated = { ...prev };
      delete updated[token];
      return updated;
    });
    setSavedAddresses(prev => {
      const updated = { ...prev };
      delete updated[token];
      return updated;
    });
    toast.success('Address Removed', {
      description: `${token} address has been removed.`,
      duration: 3000,
    });
  };

  const copyExample = (token: string) => {
    const example = UTXO_TOKENS.find(t => t.symbol === token)?.example || '';
    if (example) {
      navigator.clipboard.writeText(example);
      toast.info('Example Copied', {
        description: 'Paste this example to see the format (replace with your actual address)',
        duration: 3000,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Add Your UTXO Addresses
          </DialogTitle>
          <DialogDescription>
            WalletConnect only shows your EVM address. Add your UTXO addresses (RVN, BTC, etc.) to see balances. 
            <strong className="block mt-2 text-amber-400">Note:</strong> You'll still need to manually send funds to deposit addresses when swapping UTXO tokens.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Info Alert */}
          <Alert className="border-blue-500/50 bg-blue-500/10">
            <Info className="h-4 w-4 text-blue-400" />
            <AlertDescription className="text-sm">
              <strong>Why add UTXO addresses?</strong>
              <p className="text-xs mt-1 mb-2">
                WalletConnect (used by Trust Wallet, MetaMask, etc.) only exposes EVM addresses. 
                Adding your UTXO addresses here allows the swap interface to display your actual balances.
              </p>
              <strong className="block mt-2">How to find your addresses in Trust Wallet:</strong>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-xs">
                <li>Open Trust Wallet app</li>
                <li>Tap on the token (e.g., Ravencoin, Bitcoin)</li>
                <li>Tap "Receive" button</li>
                <li>Copy the address shown (QR code or text)</li>
                <li>Paste it in the field below</li>
              </ol>
            </AlertDescription>
          </Alert>

          {/* Important Note */}
          <Alert className="border-amber-500/50 bg-amber-500/10">
            <AlertCircle className="h-4 w-4 text-amber-400" />
            <AlertDescription className="text-sm">
              <strong>Important:</strong> Even after adding addresses, you cannot directly sign UTXO transactions through WalletConnect. 
              When swapping UTXO tokens, you'll receive a deposit address and need to manually send funds from your wallet app.
            </AlertDescription>
          </Alert>

          {/* Address Inputs */}
          {UTXO_TOKENS.map((token) => {
            const hasAddress = !!savedAddresses[token.symbol];
            return (
              <div key={token.symbol} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor={token.symbol} className="text-sm font-medium">
                    {token.name} ({token.symbol})
                  </Label>
                  {hasAddress && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-green-400 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Saved
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => handleRemove(token.symbol)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    id={token.symbol}
                    placeholder={token.placeholder}
                    value={addresses[token.symbol] || ''}
                    onChange={(e) => handleAddressChange(token.symbol, e.target.value)}
                    className="flex-1"
                  />
                  {token.example && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyExample(token.symbol)}
                      className="shrink-0"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Warning */}
          <Alert variant="destructive" className="border-yellow-500/50 bg-yellow-500/10">
            <AlertCircle className="h-4 w-4 text-yellow-400" />
            <AlertDescription className="text-sm">
              <strong>Important:</strong> Only enter addresses you own. These addresses are stored locally in your browser and never sent to any server.
            </AlertDescription>
          </Alert>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1"
            >
              Save Addresses
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Memoize component to prevent unnecessary re-renders
export default AddUTXOAddressesModal;

